import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { MenuItemCard } from './MenuItemCard';
import { ItemDetailsModal } from './ItemDetailsModal';
import { CustomerInfoForm } from './CustomerInfoForm';
import { fetchMenuRequest } from '../../redux/slices/menuSlice';
import { setSelectedRestaurantId } from '../../redux/slices/restaurantSlice';
import { parsePrice } from '../../utils/priceUtils';

interface CreateOrderTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  apiKey: string;
}

interface OrderItem {
  name: string;
  quantity: string;
  itemPrice: string;
  modifiers: Array<{
    modifier_name: string;
    modifier_price: string;
  }>;
}

interface CustomerInfo {
  name: string;
  phone: string;
  email: string;
  specialRequests: string;
}

export const CreateOrderTestModal: React.FC<CreateOrderTestModalProps> = ({
  isOpen,
  onClose,
  restaurantId: initialRestaurantId,
  apiKey: initialApiKey,
}) => {
  const dispatch = useAppDispatch();
  const { apiBaseUrl, environment } = useAppSelector((state) => state.auth);
  const { menuItems = [], loading: menuLoading } = useAppSelector((state) => state.menu);
  const { restaurants } = useAppSelector((state) => state.restaurant);

  // Local state for API key and restaurant
  const [apiKey, setApiKey] = useState(initialApiKey);
  const [restaurantId, setRestaurantId] = useState(initialRestaurantId);
  const [selectedRestaurantName, setSelectedRestaurantName] = useState('');

  // Initialize restaurant name on mount or when restaurants/restaurantId changes
  useEffect(() => {
    if (restaurantId && restaurants.length > 0) {
      const restaurant = restaurants.find(r => r.restaurantId === restaurantId);
      if (restaurant) {
        setSelectedRestaurantName(restaurant.name);
      }
    }
  }, [restaurantId, restaurants]);

  // Prepopulate API key when modal opens
  useEffect(() => {
    if (isOpen && initialApiKey) {
      setApiKey(initialApiKey);
    }
  }, [isOpen, initialApiKey]);

  // Customer info state
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    phone: '',
    email: '',
    specialRequests: '',
  });

  // Order items state
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  
  // Quantity tracking for menu items
  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>({});

  // Modal states
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isItemDetailsOpen, setIsItemDetailsOpen] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // API response state
  const [response, setResponse] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // Collapsible sections
  const [authExpanded, setAuthExpanded] = useState(true);
  const [bodyParamsExpanded, setBodyParamsExpanded] = useState(true);
  const [menuItemsExpanded, setMenuItemsExpanded] = useState(true);
  const [customerInfoExpanded, setCustomerInfoExpanded] = useState(true);


  // Pre-filter menu items (base filter - only enabled items)
  const availableMenuItems = useMemo(() => {
    if (!menuItems) return [];
    return menuItems.filter(item => item.isEnabled);
  }, [menuItems]);

  // Filter menu items (combined filters - like MenuPage)
  const filteredMenuItems = useMemo(() => {
    if (!availableMenuItems) return [];

    let filtered = availableMenuItems;

    // Apply category filter (multi-level support)
    if (selectedCategory) {
      filtered = filtered.filter(item =>
        item.category === selectedCategory ||
        item.subCategory === selectedCategory ||
        item.level1Category === selectedCategory ||
        item.level2Category === selectedCategory
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
      );
    }

    // Sort by name
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [availableMenuItems, selectedCategory, searchQuery]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    menuItems.forEach(item => {
      if (item.isEnabled) {
        if (item.category) cats.add(item.category);
        if (item.subCategory) cats.add(item.subCategory);
      }
    });
    return Array.from(cats).sort();
  }, [menuItems]);

  // Check if item needs customization (only if it has REQUIRED modifiers)
  const itemNeedsCustomization = useCallback((item: any) => {
    // Check for REQUIRED modifiers (isRequired, required, or isForced)
    const hasRequiredModifiers = item.modifierList?.some((mod: any) => 
      mod.isRequired === true || mod.required === true || mod.isForced === true
    );
    return hasRequiredModifiers;
  }, []);

  // Handle add button click
  const handleAddClick = useCallback((item: any) => {
    if (itemNeedsCustomization(item)) {
      // Find the full item from menuItems to ensure we have all properties
      const fullItem = menuItems.find(menuItem => menuItem.pkId === item.pkId) || item;
      setSelectedItem(fullItem);
      setIsItemDetailsOpen(true);
    } else {
      // Update quantity tracking
      setItemQuantities(prev => ({
        ...prev,
        [item.pkId]: (prev[item.pkId] || 0) + 1
      }));
    }
  }, [itemNeedsCustomization, menuItems]);

  // Handle increment quantity
  const handleIncrement = useCallback((item: any) => {
    const menuItem = menuItems.find(mi => mi.pkId === item.pkId);
    if (!menuItem) return;
    
    // Check if item exists in orderItems (with modifiers)
    const orderItemIndex = orderItems.findIndex(oi => oi.name === menuItem.name);
    
    if (orderItemIndex !== -1) {
      // Item has modifiers - increase quantity in orderItems
      setOrderItems(prev => {
        const updated = [...prev];
        updated[orderItemIndex] = {
          ...updated[orderItemIndex],
          quantity: (parseInt(updated[orderItemIndex].quantity) + 1).toString()
        };
        return updated;
      });
    } else {
      // Simple item - increase in itemQuantities
      setItemQuantities(prev => ({
        ...prev,
        [item.pkId]: (prev[item.pkId] || 0) + 1
      }));
    }
  }, [menuItems, orderItems]);

  // Handle decrement quantity
  const handleDecrement = useCallback((item: any) => {
    const menuItem = menuItems.find(mi => mi.pkId === item.pkId);
    if (!menuItem) return;
    
    // Check if item exists in orderItems (with modifiers)
    const orderItemIndex = orderItems.findIndex(oi => oi.name === menuItem.name);
    
    if (orderItemIndex !== -1) {
      // Item has modifiers - decrease quantity in orderItems
      setOrderItems(prev => {
        const orderItem = prev[orderItemIndex];
        const qty = parseInt(orderItem.quantity);
        
        if (qty <= 1) {
          // Remove item
          return prev.filter((_, i) => i !== orderItemIndex);
        } else {
          // Decrease quantity
          const updated = [...prev];
          updated[orderItemIndex] = {
            ...orderItem,
            quantity: (qty - 1).toString()
          };
          return updated;
        }
      });
    } else {
      // Simple item - decrease in itemQuantities
      const currentQty = itemQuantities[item.pkId] || 0;
      
      if (currentQty <= 1) {
        // Remove from quantities
        setItemQuantities(prev => {
          const newQuantities = { ...prev };
          delete newQuantities[item.pkId];
          return newQuantities;
        });
      } else {
        // Decrease quantity
        setItemQuantities(prev => ({
          ...prev,
          [item.pkId]: currentQty - 1
        }));
      }
    }
  }, [menuItems, orderItems, itemQuantities]);
  
  // Generate consolidated order items from quantities
  const consolidatedOrderItems = useMemo(() => {
    return Object.entries(itemQuantities).map(([pkId, quantity]) => {
      const menuItem = menuItems.find(item => item.pkId === pkId);
      if (!menuItem) return null;
      
      return {
        name: menuItem.name,
        quantity: quantity.toString(),
        itemPrice: parsePrice(menuItem.price).toFixed(2),
        modifiers: [],
      };
    }).filter(Boolean) as OrderItem[];
  }, [itemQuantities, menuItems]);

  // Add item to order from modal
  const handleAddItemToOrder = useCallback((orderItem: OrderItem) => {
    setOrderItems(prev => {
      // Find existing item with same name and modifiers
      const existingIndex = prev.findIndex(item => 
        item.name === orderItem.name &&
        JSON.stringify(item.modifiers) === JSON.stringify(orderItem.modifiers)
      );
      
      if (existingIndex !== -1) {
        // Item exists - increase quantity
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: (parseInt(updated[existingIndex].quantity) + parseInt(orderItem.quantity)).toString()
        };
        return updated;
      } else {
        // New item - add to array
        return [...prev, orderItem];
      }
    });
    
    setIsItemDetailsOpen(false);
  }, []);
  
  // Calculate total quantity for an item (from both itemQuantities and orderItems)
  const getItemTotalQuantity = useCallback((pkId: string) => {
    const menuItem = menuItems.find(item => item.pkId === pkId);
    if (!menuItem) return 0;
    
    // Count from itemQuantities (simple items without modifiers)
    const simpleQty = itemQuantities[pkId] || 0;
    
    // Count from orderItems (items WITH modifiers)
    const modifierQty = orderItems
      .filter(item => item.name === menuItem.name)
      .reduce((sum, item) => sum + parseInt(item.quantity), 0);
    
    // Return total
    return simpleQty + modifierQty;
  }, [menuItems, itemQuantities, orderItems]);
  
  // Calculate total selected items count
  const totalSelectedItems = useMemo(() => {
    return Object.values(itemQuantities).reduce((sum, qty) => sum + qty, 0);
  }, [itemQuantities]);


  // Generate cURL command
  const generateCurlCommand = useCallback(() => {
    const allOrderItems = [...consolidatedOrderItems, ...orderItems];
    
    const payload = {
      name: customerInfo.name || '<customer_name>',
      phone: customerInfo.phone || '<customer_phone>',
      email: customerInfo.email || '<customer_email>',
      ...(customerInfo.specialRequests && { specialRequests: customerInfo.specialRequests }),
      orderedItems: allOrderItems.length > 0 ? allOrderItems : [
        {
          name: '<item_name>',
          quantity: '<quantity>',
          itemPrice: '<item_price>',
          modifiers: [
            {
              modifier_name: '<modifier_name>',
              modifier_price: '<modifier_price>',
            },
          ],
        },
      ],
    };

    return `curl --request POST \\
  --url ${apiBaseUrl}/createOrder \\
  --header 'API-Key: ${apiKey || '<api-key>'}' \\
  --header 'restaurant-id: ${restaurantId || '<restaurant-id>'}' \\
  --header 'Content-Type: application/json' \\
  --data '${JSON.stringify(payload, null, 2)}'`;
  }, [apiBaseUrl, apiKey, restaurantId, customerInfo, consolidatedOrderItems, orderItems]);

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Get method badge styles
  const getMethodBadgeStyles = (method: string) => {
    return 'inline-flex items-center px-3 py-1 rounded text-sm font-semibold uppercase tracking-wide bg-blue-100 text-blue-800';
  };

  // Submit order
  const handleSubmit = async () => {
    if (!customerInfo.name || !customerInfo.phone || !customerInfo.email) {
      alert('Please fill in customer information first');
      return;
    }

    const allOrderItems = [...consolidatedOrderItems, ...orderItems];
    
    if (allOrderItems.length === 0) {
      alert('Please add at least one item to the order');
      return;
    }

    setIsLoading(true);
    setResponse('');

    try {
      const payload = {
        name: customerInfo.name,
        phone: customerInfo.phone,
        email: customerInfo.email,
        ...(customerInfo.specialRequests && { specialRequests: customerInfo.specialRequests }),
        orderedItems: allOrderItems,
      };

      const res = await fetch(`${apiBaseUrl}/createOrder`, {
        method: 'POST',
        headers: {
          'API-Key': apiKey,
          'restaurant-id': restaurantId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      setResponse(JSON.stringify(data, null, 2));
    } catch (error) {
      setResponse(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 backdrop-blur-sm bg-white/30 flex items-center justify-center p-4">
      <div className="bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] max-w-7xl w-full min-w-[1200px] min-h-[600px] max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header - ApiTestModal Style */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
          {/* Left Side - Title */}
          <div className="flex items-center space-x-3">
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Create Order</h2>
          </div>
          
          {/* Right Side - Method, Endpoint and Send Button */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <span className={`text-xs px-2 py-1 rounded font-semibold ${
                environment === 'sandbox' 
                  ? 'bg-amber-100 text-amber-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {environment === 'sandbox' ? 'Sandbox' : 'Production'}
              </span>
              <span className={getMethodBadgeStyles('POST')}>
                POST
              </span>
              <code className="text-[var(--text-primary)] font-mono text-sm">
                /createOrder
              </code>
              <button
                onClick={() => copyToClipboard(`${apiBaseUrl}/createOrder`)}
                className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                title="Copy URL"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={isLoading || !customerInfo.name || !customerInfo.phone || !customerInfo.email || (consolidatedOrderItems.length === 0 && orderItems.length === 0)}
              className="px-4 py-2 bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>{isLoading ? 'Sending...' : 'Send'}</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
              </svg>
            </button>
            
            <button 
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden min-w-full min-h-[500px]">
          {/* Left Side - Authorization and Menu Selection */}
          <div className="flex-1 min-w-[600px] min-h-full p-6 overflow-y-auto border-r border-[var(--border-color)]">
            <div className="space-y-6">
              {/* Authorization Section */}
              <div className="border border-[var(--border-color)] rounded-lg">
                <button
                  onClick={() => setAuthExpanded(!authExpanded)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--bg-hover)] transition-colors"
                >
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">Authorization</h4>
                  <svg 
                    className={`w-4 h-4 transition-transform ${authExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {authExpanded && (
                  <div className="px-4 pb-4 border-t border-[var(--border-color)]">
                    <div className="mt-4 space-y-4">
                      {/* API Key Field */}
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">API-Key</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">string</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm mb-3">API key</p>
                        <input
                          type="text"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder="enter API-Key"
                          className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                        />
                      </div>

                      {/* Restaurant Field */}
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">restaurant</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">select</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm mb-3">Select restaurant from your account</p>
                        {restaurants.length > 0 ? (
                          <select
                            value={selectedRestaurantName}
                            onChange={(e) => {
                              const selectedName = e.target.value;
                              setSelectedRestaurantName(selectedName);
                              const restaurant = restaurants.find(r => r.name === selectedName);
                              if (restaurant) {
                                setRestaurantId(restaurant.restaurantId);
                                // Save to Redux state
                                dispatch(setSelectedRestaurantId(restaurant.restaurantId));
                                // Fetch menu for the selected restaurant
                                dispatch(fetchMenuRequest({ restaurantId: restaurant.restaurantId }));
                              }
                            }}
                            className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                          >
                            <option value="">Select a restaurant</option>
                            {restaurants.map((restaurant) => (
                              <option key={restaurant.restaurantId} value={restaurant.name}>
                                {restaurant.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                            No restaurants available. Please register a restaurant first.
                          </div>
                        )}
                        {restaurantId && (
                          <p className="text-[var(--text-muted)] text-xs mt-2">
                            ID: {restaurantId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Body Parameters Section */}
              <div className="border border-[var(--border-color)] rounded-lg">
                <button
                  onClick={() => setBodyParamsExpanded(!bodyParamsExpanded)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--bg-hover)] transition-colors"
                >
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">body parameter</h4>
                  <svg 
                    className={`w-4 h-4 transition-transform ${bodyParamsExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {bodyParamsExpanded && (
                  <div className="px-4 pb-4 border-t border-[var(--border-color)]">
                    <div className="mt-4 space-y-4">
                      {/* Select Menu Items - Collapsible Subsection */}
                      <div className="border border-[var(--border-color)] rounded-lg">
                        <button
                          onClick={() => setMenuItemsExpanded(!menuItemsExpanded)}
                          className="w-full flex items-center justify-between p-3 text-left hover:bg-[var(--bg-hover)] transition-colors"
                        >
                          <div className="flex items-center space-x-2">
                            <h5 className="text-sm font-semibold text-[var(--text-primary)]">Select Menu Items</h5>
                            {totalSelectedItems > 0 && (
                              <span className="px-2 py-0.5 bg-[var(--accent-color)] text-white rounded-full text-xs font-semibold">
                                {totalSelectedItems}
                              </span>
                            )}
                          </div>
                          <svg 
                            className={`w-4 h-4 transition-transform ${menuItemsExpanded ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {menuItemsExpanded && (
                          <div className="px-3 pb-3 border-t border-[var(--border-color)]">
                            <div className="mt-3 space-y-4">
                              {/* Search - Sticky */}
                              <input
                                type="text"
                                placeholder="Search menu items..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                              />

                              {/* Category Filter - Sticky */}
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => setSelectedCategory(null)}
                                  className={`px-3 py-1 rounded text-sm ${
                                    !selectedCategory
                                      ? 'bg-[var(--accent-color)] text-white'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  All
                                </button>
                                {categories.map(cat => (
                                  <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-3 py-1 rounded text-sm ${
                                      selectedCategory === cat
                                        ? 'bg-[var(--accent-color)] text-white'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {cat}
                                  </button>
                                ))}
                              </div>

                              {/* Menu Items Grid - Scrollable Only */}
                              <div className="max-h-[300px] overflow-y-auto">
                                {menuLoading ? (
                                  <div className="text-center py-8 text-[var(--text-secondary)]">Loading menu items...</div>
                                ) : filteredMenuItems.length > 0 ? (
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {filteredMenuItems.map(item => (
                                      <MenuItemCard
                                        key={item.pkId}
                                        item={{
                                          pkId: item.pkId,
                                          name: item.name,
                                          price: item.price
                                        }}
                                        quantity={getItemTotalQuantity(item.pkId)}
                                        onAdd={() => handleAddClick(item)}
                                        onIncrement={() => handleIncrement(item)}
                                        onDecrement={() => handleDecrement(item)}
                                      />
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-8 text-[var(--text-secondary)]">
                                    No menu items found
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Customer Information - Collapsible Subsection */}
                      <div className="border border-[var(--border-color)] rounded-lg">
                        <button
                          onClick={() => setCustomerInfoExpanded(!customerInfoExpanded)}
                          className="w-full flex items-center justify-between p-3 text-left hover:bg-[var(--bg-hover)] transition-colors"
                        >
                          <h5 className="text-sm font-semibold text-[var(--text-primary)]">Customer Information</h5>
                          <svg 
                            className={`w-4 h-4 transition-transform ${customerInfoExpanded ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {customerInfoExpanded && (
                          <div className="px-3 pb-3 border-t border-[var(--border-color)]">
                            <div className="mt-3">
                              <CustomerInfoForm
                                customerInfo={customerInfo}
                                onChange={setCustomerInfo}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - cURL and Response */}
          <div className="w-96 min-w-[384px] min-h-full flex-shrink-0 flex flex-col">
            {/* Top Half - cURL */}
            <div className="flex-1 min-h-0 max-h-[500px] bg-[var(--bg-secondary)] p-4 border-b border-[var(--border-color)]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[var(--text-primary)]">cURL</h4>
                <button
                  onClick={() => copyToClipboard(generateCurlCommand())}
                  className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4 h-full overflow-auto">
                <pre className="text-sm text-[var(--text-primary)] font-mono whitespace-pre-wrap">
                  <code>{generateCurlCommand()}</code>
                </pre>
              </div>
            </div>

            {/* Bottom Half - Response */}
            <div className="flex-1 min-h-0 max-h-[400px] bg-[var(--bg-secondary)] p-4 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[var(--text-primary)]">
                  {isLoading ? 'Loading Response...' : response ? 'Response' : 'Response'}
                </h4>
                {response && !isLoading && (
                  <button
                    onClick={() => copyToClipboard(response)}
                    className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                    title="Copy response"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex-1 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex items-center space-x-2 text-[var(--text-secondary)]">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading response...</span>
                    </div>
                  </div>
                ) : response ? (
                  <pre className="text-sm text-[var(--text-primary)] font-mono whitespace-pre-wrap break-words">
                    <code>{response}</code>
                  </pre>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[var(--text-muted)] text-sm">
                      Click Send to test the API endpoint
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Item Details Modal */}
      <ItemDetailsModal
        isOpen={isItemDetailsOpen}
        onClose={() => setIsItemDetailsOpen(false)}
        item={selectedItem}
        onAddToOrder={handleAddItemToOrder}
      />
    </div>
  );
};
